/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Text, View, useWindowDimensions } from 'react-native';
import { showError, showSuccess } from '../../shared/ui/appToast';
import { tabBarListContentStyle } from '../../shared/layout/tabBarListContentStyle'
import { useTabWorkspaceLayout } from '../../shared/layout/useContentLayout';
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketAlertStrip } from '../../shared/ticket/TicketAlertStrip'
import { OperationalScrollScreen } from '../../shared/ticket/OperationalScrollScreen'
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts';
import {
  useNavigation,
  useRoute,
  type NavigationProp,
  type ParamListBase,
} from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useScreenLoad } from '../../shared/ui/hooks-screen/useScreenLoad';
import { isPlaywrightE2E, isPlaywrightE2EBridge } from '../../shared/playwrightE2e';
import { CatalogProductCard } from '../components/CatalogProductCard';
import { CatalogListSkeleton } from '../components/CatalogListSkeleton';
import { CatalogDeskFooter } from '../components/CatalogDeskFooter';
import { CatalogProductDetailModal } from '../components/CatalogProductDetailModal';
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal';
import { ProductHistoryModal } from '../components/ProductHistoryModal';
import { CatalogProductFormModal } from '../components/CatalogProductFormModal';
import { CatalogScreenHeader } from '../components/CatalogScreenHeader';
import { CatalogFilterSheetContent } from '../components/CatalogFilterSheetContent';
import { CatalogListToolbar } from '../components/CatalogListToolbar';
import { FilterSheetModal } from '../../shared/filters/FilterSheetModal';
import { CatalogMastersModal } from '../components/CatalogMastersModal';
import { CatalogScanners } from '../components/CatalogScanners';
import { BulkCoefficientModal } from '../components/BulkCoefficientModal';
import { CsvImportExportModal } from '../../shared/csv/CsvImportExportModal';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { useBusinessProfileStore } from '../../shell/session/store/useBusinessProfileStore';
import { allowsMenuDietaryFilters } from '../../shell/feature-flags/businessCapabilitiesService';
import { exportLabelScalePlu } from '../../scale/application/exportLabelScalePlu';
import { getBranchScaleConfig } from '../../scale/infra/scaleConfigRepo';
import { useCatalogScreen } from './useCatalogScreen';
import { styles } from './CatalogScreen.styles';
import { useAllowIvaRate } from './hooks/useAllowIvaRate';
import { useCatalogFilterSheet } from './hooks/useCatalogFilterSheet';
import { useCatalogOverlayStore } from '../store/useCatalogOverlayStore';
import { getCatalogRamSnapshotCache } from '../infra/catalogRamSnapshotCache';
import { mapRemoteSnapshotToCatalogProductRow } from '../infra/catalogReadRepo';
import { catalogItemTypeFilterStripTitle } from '../domain/catalogFilterOptions';
import { createBrand } from '../infra/brandsRepoMutations';
import { createSupplier } from '../infra/suppliersRepoMutations';

const CATALOG_LIST_WEB_EXTRA_BOTTOM = 16;

export const CatalogScreen = () => {
  const vm = useCatalogScreen();
  const navigation = useNavigation<NavigationProp<ParamListBase>>();
  const allowIvaRate = useAllowIvaRate(vm.currentBranchId);
  const kitchenTicketsEnabled = useHasBusinessCapability('kitchen_tickets');
  const businessProfile = useBusinessProfileStore((s) => s.profile);
  const menuDietaryFiltersEnabled = allowsMenuDietaryFilters(businessProfile);
  const recipesEnabled = useHasBusinessCapability('recipes');
  const openProduce = useCallback(
    (product: { id: string; name: string }) => {
      navigation.navigate('ProduceRecipe', {
        productId: product.id,
        productName: product.name,
      });
    },
    [navigation],
  );
  const openDisassemble = useCallback(
    (product: { id: string; name: string }) => {
      navigation.navigate('DisassembleRecipe', {
        productId: product.id,
        productName: product.name,
      });
    },
    [navigation],
  );
  const refreshProductRowAfterBomSave = useCallback(
    async (productId: string | undefined) => {
      if (!productId) return;
      await vm.loadFirstPage();
      const refreshed = vm.products.find((p) => p.id === productId);
      if (!refreshed) return;
      if (vm.selectedProduct?.id === productId) {
        vm.setSelectedProduct(refreshed);
      }
      if (vm.editingProduct?.id === productId) {
        useCatalogOverlayStore.getState().setEditingProduct(refreshed);
      }
    },
    [vm],
  );
  const weightUomEnabled = useHasBusinessCapability('weight_uom');

  const handleLabelScaleExport = useCallback(async () => {
    try {
      const branchId = vm.currentBranchId ?? '';
      const cfg = branchId ? await getBranchScaleConfig(branchId) : null;
      const adapter = cfg?.labelExportAdapter ?? 'systel_csv';
      const result = await exportLabelScalePlu(adapter);
      if (!result.ok) {
        showError('Exportar a balanza', result.error);
        return;
      }
      showSuccess(
        'Archivo listo',
        `${result.count} PLU en ${result.filename}. Cargalo en iTegra o Qendra.`,
      );
    } catch (e) {
      showError(
        'Exportar a balanza',
        e instanceof Error ? e.message : 'No se pudo armar el archivo.',
      );
    }
  }, [vm.currentBranchId]);
  useEffect(() => {
    ensureTicketFonts();
  }, []);
  const pendingOpenNewService = useCatalogOverlayStore((s) => s.pendingOpenNewService);
  useEffect(() => {
    if (!pendingOpenNewService) return;
    if (!useCatalogOverlayStore.getState().consumeOpenNewServiceRequest()) return;
    void vm.openCreateModal({ itemType: 'service' });
  }, [pendingOpenNewService, vm.openCreateModal]);
  const pendingOpenNewProduct = useCatalogOverlayStore((s) => s.pendingOpenNewProduct);
  useEffect(() => {
    if (!pendingOpenNewProduct) return;
    if (!useCatalogOverlayStore.getState().consumeOpenNewProductRequest()) return;
    void vm.openCreateModal({ itemType: 'physical' });
  }, [pendingOpenNewProduct, vm.openCreateModal]);
  const pendingOpenMastersSection = useCatalogOverlayStore((s) => s.pendingOpenMastersSection);
  useEffect(() => {
    if (!pendingOpenMastersSection) return;
    const section = useCatalogOverlayStore.getState().consumeOpenMastersRequest();
    if (!section) return;
    vm.openMastersHub(section);
  }, [pendingOpenMastersSection, vm.openMastersHub]);
  const pendingOpenProductDetail = useCatalogOverlayStore((s) => s.pendingOpenProductDetail);
  useEffect(() => {
    if (!pendingOpenProductDetail) return;
    const { productId, tab, recipeKind } = pendingOpenProductDetail;
    const cached = getCatalogRamSnapshotCache()?.products.find((p) => p.id === productId);
    const row =
      vm.products.find((p) => p.id === productId) ??
      (cached ? mapRemoteSnapshotToCatalogProductRow(cached, undefined) : null);
    if (!row) return;
    if (!useCatalogOverlayStore.getState().consumeOpenProductDetailRequest()) return;
    vm.openProductDetail(row, { tab, recipeKind });
  }, [pendingOpenProductDetail, vm.products, vm.openProductDetail]);
  const pendingCatalogItemTypeFilter = useCatalogOverlayStore(
    (s) => s.pendingCatalogItemTypeFilter,
  );
  useEffect(() => {
    if (!pendingCatalogItemTypeFilter) return;
    const filter = useCatalogOverlayStore.getState().consumeCatalogItemTypeFilterRequest();
    if (!filter) return;
    vm.setItemTypeFilter(filter);
  }, [pendingCatalogItemTypeFilter, vm.setItemTypeFilter]);
  const route = useRoute();
  const insets = useSafeAreaInsets();
  const { layoutWide: isWideCatalogLayout } = useTabWorkspaceLayout();
  const { width: screenWidth } = useWindowDimensions();
  const catalogGridColumns = !isWideCatalogLayout && screenWidth > 600 ? 2 : 1;

  const { refreshing, onRefresh } = useScreenLoad(
    route.name,
    async (opts) => { await vm.loadFn(opts); }
  );

  useEffect(() => {
    if (!(isPlaywrightE2E || isPlaywrightE2EBridge())) return;
    window.__VUELTO_E2E_FORCE_CATALOG_SYNC__ = async () => {
      await vm.loadFn({ force: true });
    };
    return () => {
      delete window.__VUELTO_E2E_FORCE_CATALOG_SYNC__;
    };
  }, [vm.loadFn]);

  const listContentStyle = useMemo(
    () =>
      tabBarListContentStyle({
        bottomInset: insets.bottom,
        webExtraBottom: CATALOG_LIST_WEB_EXTRA_BOTTOM,
        baseContentStyle: styles.listContent,
        emptyGrowStyle: vm.products.length === 0 ? styles.listContentEmptyGrow : null,
      }),
    [insets.bottom, vm.products.length]
  );

  const wideScrollContentStyle = useMemo(
    () =>
      tabBarListContentStyle({
        bottomInset: insets.bottom,
        webExtraBottom: CATALOG_LIST_WEB_EXTRA_BOTTOM,
        baseContentStyle: styles.listContent,
      }),
    [insets.bottom]
  );

  const {
    filterSheetOpen,
    setFilterSheetOpen,
    filterDraft,
    setFilterDraft,
    applyFilterDraft,
    clearFilterDraft,
    filterTrigger,
    hasActiveDraft,
  } = useCatalogFilterSheet(vm, isWideCatalogLayout);

  const catalogListHeader = (
    <>
      <CatalogScreenHeader
        searchQuery={vm.searchQuery}
        onSearchQueryChange={vm.onCatalogSearchFieldChange}
        onOpenSearchScanner={() => vm.setSearchScannerVisible(true)}
        onOpenCreateModal={() => void vm.openCreateModal()}
        newItemLabel={vm.chromeCopy.newItemLabel}
        newItemShortLabel={vm.chromeCopy.newItemShortLabel}
        kpiItemsLabel={vm.chromeCopy.kpiItemsLabel}
        kpiOnMenuLabel={vm.chromeCopy.visibleOnlineLabel}
        moduleKicker="Catálogo"
        moduleTitle={vm.chromeCopy.moduleTitle}
        moduleMeta="Acá cargás y editás lo que vendés."
        onOpenCategoriesManager={vm.openMastersHub}
        onOpenBulkPriceModal={vm.openBulkPriceModal}
        onOpenCsvModal={vm.canImportExport ? vm.openCsvModal : undefined}
        onOpenLabelScaleExport={
          weightUomEnabled && vm.canEdit
            ? () => {
                void handleLabelScaleExport();
              }
            : undefined
        }
        canEdit={vm.canEdit}
        canManageMasters={vm.canManageMasters}
        filterTrigger={filterTrigger}
        totalProductCount={vm.kpis}
        lookupFeedback={vm.lookupFeedback}
        remoteSuggestion={vm.remoteSuggestion}
        getCategoryLabel={vm.getCategoryLabel}
        onUseRemoteSuggestion={vm.useRemoteSuggestion}
        onDismissRemoteSuggestion={vm.dismissRemoteSuggestion}
        showBarcodeCameraButton={!isWideCatalogLayout && vm.barcodeAllowCamera}
        onSearchSubmitEditing={vm.barcodeAllowHardware ? vm.handleSearchHardwareSubmit : undefined}
        branchId={vm.currentBranchId ?? null}
        onPickSearchSuggestion={vm.handlePickCatalogSearchSuggestion}
        pinnedCatalogProduct={vm.pinnedCatalogProduct}
        onClearPinnedCatalogSearch={vm.clearPinnedCatalogSearch}
        alertsOnPage={vm.kpisAlerts}
        incompleteOnPage={vm.kpisIncomplete}
        onMenuCount={vm.kpisOnMenu}
        servicesCount={vm.kpisServices}
        serviceCatalogEnabled={vm.serviceCatalogEnabled}
        catalogTotalCount={vm.catalogTotalCount}
        hasActiveFilters={vm.hasActiveFilters}
        onToggleAlertFilter={() => vm.setAlertFilter(!vm.alertFilter)}
        alertFilterActive={vm.alertFilter}
        showStockAlertKpi={!vm.hideStockPanels}
        onToggleIncompleteFilter={() => vm.setIncompleteFilter(!vm.incompleteFilter)}
        incompleteFilterActive={vm.incompleteFilter}
        onToggleServicesKpi={() =>
          vm.setItemTypeFilter(vm.itemTypeFilter === 'service' ? 'all' : 'service')
        }
        servicesKpiSelected={vm.itemTypeFilter === 'service'}
      />
    </>
  );

  /**
   * Strip entre KPIs y lista: solo cuando el filtro está activo (salida clara
   * «volver al catálogo completo»). Los tiles Alertas/Incompletos del KPI
   * strip ya son el entry point — sin banner proactivo duplicado.
   */
  const catalogAlertStrip = (() => {
    if (vm.alertFilter) {
      return (
        <TicketAlertStrip
          title="Viendo solo alertas"
          subtitle="Ver todo el catálogo"
          tone="warn"
          onPress={() => vm.setAlertFilter(false)}
          testID="catalog.alert.strip"
          accessibilityLabel="Quitar filtro de alertas"
        />
      );
    }
    if (vm.incompleteFilter) {
      return (
        <TicketAlertStrip
          title="Viendo solo incompletos"
          subtitle="Ver todo el catálogo"
          tone="warn"
          onPress={() => vm.setIncompleteFilter(false)}
          testID="catalog.incomplete.strip"
          accessibilityLabel="Quitar filtro de incompletos"
        />
      );
    }
    const itemTypeStrip = catalogItemTypeFilterStripTitle(vm.itemTypeFilter);
    if (itemTypeStrip) {
      return (
        <TicketAlertStrip
          title={itemTypeStrip}
          subtitle="Ver todo el catálogo"
          tone="default"
          onPress={() => vm.setItemTypeFilter('all')}
          testID="catalog.itemtype.strip"
          accessibilityLabel="Quitar filtro de tipo"
        />
      );
    }
    return null;
  })();

  const catalogPagination = (
    <CatalogDeskFooter
      pageIndex={vm.catalogPageIndex}
      pageSize={vm.pageSize}
      productCount={vm.products.length}
      totalCount={vm.catalogTotalCount}
      hasMore={vm.hasMore}
      loading={vm.catalogLoading}
      onPreviousPage={vm.loadCatalogPreviousPage}
      onNextPage={vm.loadCatalogNextPage}
      onGoToPage={vm.goToCatalogPage}
      embedded
    />
  );

  const emptyTitle = vm.hasActiveFilters
    ? 'Nada coincide con los filtros'
    : vm.catalogTotalCount === 0
      ? vm.chromeCopy.emptyTitle
      : 'No encontramos nada';
  const emptySubtitle = vm.hasActiveFilters
    ? 'Cambiá la búsqueda o borrá filtros.'
    : vm.canEdit
      ? vm.chromeCopy.emptySubtitleCanEdit
      : 'Todavía no hay nada acá.';
  const isTrueEmpty = !vm.hasActiveFilters && vm.catalogTotalCount === 0;
  const emptyState = vm.catalogLoading && vm.products.length === 0 ? (
    <CatalogListSkeleton rows={vm.pageSize} />
  ) : vm.catalogError ? (
    // Error ≠ empty: nunca mostrar «Cargá tu primer ítem» cuando falló el fetch.
    <ModuleEmptyState
      loading={false}
      variant="nest"
      kicker="Catálogo"
      title="No se pudo cargar el catálogo"
      subtitle="Revisá tu conexión y probá de nuevo."
      testID="catalog.error"
      primaryAction={{
        label: 'Reintentar',
        onPress: () => void vm.loadFirstPage(),
        testID: 'catalog.retry',
      }}
    />
  ) : isTrueEmpty && vm.canEdit ? (
    <ModuleEmptyState
      loading={false}
      variant="nest"
      kicker="Catálogo"
      title={emptyTitle}
      subtitle="Nombre y precio alcanzan. El stock va en Compras."
      testID="catalog.empty"
      primaryAction={{
        label: vm.chromeCopy.emptyPrimaryAction,
        onPress: () => vm.openCreateModal(),
        testID: 'catalog.empty.create',
      }}
      secondaryAction={
        vm.canImportExport
          ? { label: vm.chromeCopy.emptySecondaryCsvAction, onPress: vm.openCsvModal }
          : vm.barcodeAllowCamera
            ? { label: 'Escanear código', onPress: () => vm.setSearchScannerVisible(true) }
            : undefined
      }
    />
  ) : (
    <ModuleEmptyState
      loading={false}
      loadingText="Cargando catálogo…"
      variant="nest"
      kicker="Catálogo"
      title={emptyTitle}
      subtitle={emptySubtitle}
      testID="catalog.empty"
      secondaryAction={
        vm.hasActiveFilters
          ? { label: 'Borrar filtros', onPress: vm.clearFilters }
          : undefined
      }
      primaryAction={
        vm.canEdit ? { label: vm.chromeCopy.emptyPrimaryAction, onPress: () => vm.openCreateModal() } : undefined
      }
    />
  );

  const catalogProductRows =
    vm.products.length > 0 ? (
      vm.products.map((item, index) => {
        const grouped = catalogGridColumns === 1;
        const group = !grouped
          ? ('solo' as const)
          : index === vm.products.length - 1
            ? ('groupedLast' as const)
            : ('grouped' as const);
        return (
          <View
            key={item.id}
            style={catalogGridColumns > 1 ? styles.catalogGridCell : undefined}
          >
            <CatalogProductCard
              product={item}
              categoryLabel={vm.getCategoryLabel(item.categoryId)}
              formatCurrency={vm.currencyFormatter.format}
              onOpenDetail={vm.openProductDetail}
              group={group}
              showIncompleteMeta={vm.incompleteFilter}
              searchQuery={vm.searchQuery}
              visibleOnlineLabel={vm.chromeCopy.visibleOnlineLabel}
              hideStockSignals={vm.hideStockPanels}
              recipesEnabled={recipesEnabled}
            />
          </View>
        );
      })
    ) : (
      emptyState
    );

  const catalogMain = (
    <>
      {catalogListHeader}
      {catalogAlertStrip}
      {(vm.kpis ?? 0) > 0 ? (
        <CatalogListToolbar
          categories={vm.categoryFilters}
          selectedCategoryId={vm.categoryFilter}
          onSelectCategory={vm.setCategoryFilter}
          sortBy={vm.sortBy}
          onSelectSort={vm.setSortBy}
        />
      ) : null}
      <PaperSheet style={styles.listSheet} testID="catalog.list.sheet">
        {catalogGridColumns > 1 && vm.products.length > 0 ? (
          <View style={styles.catalogGridWrap}>{catalogProductRows}</View>
        ) : (
          catalogProductRows
        )}
        {catalogPagination}
      </PaperSheet>
    </>
  );

  return (
    <OperationalScrollScreen
      header={null}
      notices={null}
      body={catalogMain}
      scrollRef={vm.catalogWideScrollRef}
      refreshing={refreshing}
      onRefresh={onRefresh}
      contentContainerStyle={isWideCatalogLayout ? wideScrollContentStyle : listContentStyle}
      scrollStyle={styles.list}
      stackGap={styles.stack.gap as number | undefined}
      containerStyle={styles.container}
      containerCompactStyle={styles.containerCompact}
      layoutWide={isWideCatalogLayout}
      modals={
        <>
          <CatalogProductDetailModal
        layoutWide={isWideCatalogLayout}
        visible={vm.selectedProduct !== null}
        product={vm.selectedProduct}
        categoryLabel={vm.selectedCategoryLabel}
        categoryRestricted={vm.getCategoryIsRestricted(vm.selectedProduct?.categoryId)}
        currentBranchName={vm.currentBranch?.name ?? 'Sucursal actual'}
        currentBranchId={vm.currentBranchId ?? null}
        stock={vm.selectedProductStock}
        stockLoading={vm.selectedProductStockLoading}
        stockFailed={vm.selectedProductStockFailed}
        onRetryStock={vm.retrySelectedProductStock}
        pharmacyFieldsEnabled={vm.pharmacyFieldsEnabled}
        canEdit={vm.canEdit}
        selectedProductMarginValue={vm.selectedProductMarginValue}
        selectedProductMarginPercent={vm.selectedProductMarginPercent}
        currencyFormatter={vm.currencyFormatter}
        formatUpdatedAt={vm.formatUpdatedAt}
        brandOptions={vm.catalogBrandOptions}
        supplierOptions={vm.catalogSupplierOptions}
        onComboCompositionSaved={() => refreshProductRowAfterBomSave(vm.selectedProduct?.id)}
        onRecipeSaved={() => refreshProductRowAfterBomSave(vm.selectedProduct?.id)}
        onClose={() => {
          vm.closeProductConfirm();
          vm.closeProductDetail();
        }}
        confirmOverlay={
          vm.productConfirmModalProps
            ? {
                ...vm.productConfirmModalProps,
                onClose: vm.closeProductConfirm,
                onConfirm: vm.confirmProductAction,
              }
            : null
        }
        onEdit={vm.openEditModal}
        onDuplicate={vm.handleRequestDuplicate}
        onDeactivate={vm.handleDeactivate}
        onReactivate={vm.handleRequestReactivate}
        onViewHistory={vm.openProductHistory}
        onProduce={recipesEnabled ? openProduce : undefined}
        onDisassemble={recipesEnabled ? openDisassemble : undefined}
        hideStockPanels={vm.hideStockPanels}
      />
      {vm.selectedProduct == null && vm.productConfirmModalProps ? (
        <OperationalConfirmModal
          visible
          presentation="modal"
          {...vm.productConfirmModalProps}
          onClose={vm.closeProductConfirm}
          onConfirm={vm.confirmProductAction}
        />
      ) : null}

      <ProductHistoryModal
        layoutWide={isWideCatalogLayout}
        visible={vm.productHistoryVisible}
        productName={vm.productHistoryProduct?.name ?? ''}
        events={vm.productCatalogHistoryRows}
        historyLoading={vm.productHistoryLoading}
        historyLoadingMore={vm.productHistoryLoadingMore}
        historyHasMore={vm.productHistoryHasMore}
        onLoadMore={vm.loadMoreProductHistory}
        onClose={vm.closeProductHistory}
        actorLabel={vm.actorLabel}
        eventOptions={vm.productHistoryEventOptions}
        eventFilter={vm.productHistoryEventFilter}
        onEventFilterChange={vm.setProductHistoryEventFilter}
        fromDate={vm.productHistoryFromDate}
        toDate={vm.productHistoryToDate}
        onFromDateChange={vm.setProductHistoryFromDate}
        onToDateChange={vm.setProductHistoryToDate}
        onApplyFilters={vm.applyProductHistoryFilters}
        onClearFilters={vm.clearProductHistoryFilters}
        onExportCsv={vm.exportProductCatalogHistoryCsv}
        eventSummary={vm.formatEventSummary}
      />

      <CatalogProductFormModal
        visible={vm.modalVisible}
        serviceCatalogEnabled={vm.serviceCatalogEnabled}
        editingProduct={vm.editingProduct}
        formMode={vm.formMode}
        formCompact={vm.formCompact}
        onExpandForm={vm.expandCreateForm}
        formSuggestion={vm.formSuggestion}
        draft={vm.draft}
        onNameChange={(value) => vm.setDraft((current) => ({ ...current, name: value }))}
        onBarcodeChange={(value) => vm.setDraft((current) => ({ ...current, barcode: value.toUpperCase() }))}
        onSkuChange={(value) => vm.setDraft((current) => ({ ...current, sku: value.toUpperCase() }))}
        onPriceChange={(value) => vm.setDraft((current) => ({ ...current, price: value }))}
        onItemTypeChange={vm.handleProductItemTypeChange}
        onCategoryChange={(categoryId) => vm.setDraft((current) => ({ ...current, categoryId }))}
        categoryOptions={vm.formCategoryOptions}
        priceMarginPercentInput={vm.priceMarginPercentInput}
        onPriceMarginPercentChange={vm.setPriceMarginPercentInput}
        codeValidationMessage={vm.codeValidationMessage}
        barcodeValidationMessage={vm.barcodeValidationMessage}
        priceValidationMessage={vm.priceValidationMessage}
        draftValidationMessage={vm.draftValidationMessage ?? null}
        isDraftReady={vm.isDraftReady}
        saving={vm.saving}
        currentBranchName={vm.currentBranch?.name ?? 'Sucursal actual'}
        currentBranchId={vm.currentBranchId ?? null}
        editingProductStockLoading={vm.editingProductStockLoading}
        editingProductStockFailed={vm.editingProductStockFailed}
        onRetryEditingProductStock={vm.retryEditingProductStock}
        onComboCompositionSaved={() => refreshProductRowAfterBomSave(vm.editingProduct?.id)}
        comboDraftComponents={vm.comboDraftComponents}
        onComboDraftChange={vm.setComboDraftComponents}
        comboPickOrganizationId={vm.planEnt?.organization_id ?? null}
        onRecipeSaved={() => refreshProductRowAfterBomSave(vm.editingProduct?.id)}
        onRecipeProduce={() => {
          const p = vm.editingProduct;
          if (!p) return;
          vm.closeModal();
          openProduce(p);
        }}
        onRecipeDisassemble={() => {
          const p = vm.editingProduct;
          if (!p) return;
          vm.closeModal();
          openDisassemble(p);
        }}
        editingProductCost={vm.editingProductCost}
        canEditCost={vm.canEditCost}
        editingProductCurrentMarginValue={vm.editingProductCurrentMarginValue}
        editingProductCurrentMarginPercent={vm.editingProductCurrentMarginPercent}
        editingProductSuggestedPrice={vm.editingProductSuggestedPrice}
        currencyFormatter={vm.currencyFormatter}
        onClose={vm.closeModal}
        onSave={vm.handleSave}
        onSaveAndContinue={vm.handleSaveAndContinue}
        showSaveAndContinue={vm.formMode === 'create'}
        onSaveAttemptWhenInvalid={(message) => showError('Faltan datos', message)}
        onOpenScanner={() => vm.setFormScannerVisible(true)}
        onApplySuggestedPrice={vm.applySuggestedPrice}
        showBarcodeCameraButton={vm.barcodeAllowCamera}
        onBarcodeSubmitEditing={vm.barcodeAllowHardware ? vm.handleFormBarcodeHardwareSubmit : undefined}
        autoFocusBarcodeOnWeb={isWideCatalogLayout}
        canCreateCategory={vm.canEdit}
        onCreateCategory={() => {
          vm.closeModal();
          vm.openMastersHub('categories');
        }}
        canCreateBrand={vm.canEdit}
        onCreateBrand={() => {
          vm.closeModal();
          vm.openMastersHub('brands');
        }}
        onCreateBrandInline={
          vm.canEdit
            ? async (name) => {
                const id = await createBrand({ name });
                vm.setDraft((c) => ({ ...c, brandId: id }));
                showSuccess('Marca creada', `"${name.trim()}" ya está en tus marcas.`);
              }
            : undefined
        }
        canCreateSupplier={vm.canEdit}
        onCreateSupplier={() => {
          vm.closeModal();
          vm.openMastersHub('suppliers');
        }}
        onCreateSupplierInline={
          vm.canEdit
            ? async (name) => {
                const id = await createSupplier({ name });
                vm.setDraft((c) => ({ ...c, preferredSupplierId: id }));
                showSuccess('Proveedor creado', `"${name.trim()}" ya está en tus proveedores.`);
              }
            : undefined
        }
        supplierOptions={vm.catalogSupplierOptions}
        brandOptions={vm.catalogBrandOptions}
        onBrandIdChange={(value) => vm.setDraft((c) => ({ ...c, brandId: value }))}
        onInternalNotesChange={(value) => vm.setDraft((c) => ({ ...c, internalNotes: value }))}
        onPublicDescriptionChange={(value) => vm.setDraft((c) => ({ ...c, publicDescription: value }))}
        onMinOrderQtyChange={(value) => vm.setDraft((c) => ({ ...c, minOrderQty: value }))}
        onMaxOrderQtyChange={(value) => vm.setDraft((c) => ({ ...c, maxOrderQty: value }))}
        onIsPublicChange={vm.handleIsPublicChange}
        onMenuAvailableChange={vm.handleMenuAvailableChange}
        onVisibleOnMenuCartaChange={vm.handleVisibleOnMenuCartaChange}
        onActiveIngredientChange={(value) => vm.setDraft((c) => ({ ...c, activeIngredient: value }))}
        onUsageTagsChange={(value) => vm.setDraft((c) => ({ ...c, usageTags: value }))}
        onAllergensChange={(value) => vm.setDraft((c) => ({ ...c, allergens: value }))}
        pharmacyFieldsEnabled={vm.pharmacyFieldsEnabled}
        onPreferredSupplierIdChange={(value) => vm.setDraft((c) => ({ ...c, preferredSupplierId: value }))}
        onSupplierItemCodeChange={(value) => vm.setDraft((c) => ({ ...c, supplierItemCode: value }))}
        allowIvaRate={allowIvaRate}
        weightUomEnabled={weightUomEnabled}
        onIvaRateChange={(ivaRate) => vm.setDraft((c) => ({ ...c, ivaRate }))}
        onUnitOfMeasureChange={(unitOfMeasure) => vm.setDraft((c) => ({ ...c, unitOfMeasure }))}
        onDurationMinutesChange={(durationMinutes) => vm.setDraft((c) => ({ ...c, durationMinutes }))}
        onBufferBeforeMinutesChange={(bufferBeforeMinutes) =>
          vm.setDraft((c) => ({ ...c, bufferBeforeMinutes }))
        }
        onBufferAfterMinutesChange={(bufferAfterMinutes) =>
          vm.setDraft((c) => ({ ...c, bufferAfterMinutes }))
        }
        onBookingModeChange={(bookingMode) => vm.setDraft((c) => ({ ...c, bookingMode }))}
        onBookingPaymentModeChange={(bookingPaymentMode) =>
          vm.setDraft((c) => ({ ...c, bookingPaymentMode }))
        }
        onBookingDepositAmountChange={(bookingDepositAmount) =>
          vm.setDraft((c) => ({ ...c, bookingDepositAmount }))
        }
        onBookingDepositPercentChange={(bookingDepositPercent) =>
          vm.setDraft((c) => ({ ...c, bookingDepositPercent }))
        }
        onEvaluationProductIdChange={(evaluationProductId) =>
          vm.setDraft((c) => ({ ...c, evaluationProductId }))
        }
        onKitchenDestinationChange={(kitchenDestination) =>
          vm.setDraft((c) => ({ ...c, kitchenDestination }))
        }
        onPrepMinutesChange={(prepMinutes) =>
          vm.setDraft((c) => ({ ...c, prepMinutes }))
        }
        kitchenTicketsEnabled={kitchenTicketsEnabled}
        menuDietaryFiltersEnabled={menuDietaryFiltersEnabled}
        serviceProductOptions={vm.serviceProductOptions}
        serviceProductOptionsStatus={vm.serviceProductOptionsStatus}
        onRetryServiceProductOptions={vm.retryServiceProductOptions}
        formSession={vm.formSession}
        hideStockPanels={vm.hideStockPanels}
        variantsEnabled={vm.variantsEnabled}
        variantDrafts={vm.variantDrafts}
        parentStockQty={vm.parentStockQty}
        onAddVariantDraft={vm.addVariantDraft}
        onUpdateVariantDraft={vm.updateVariantDraft}
        onRemoveVariantDraft={vm.removeVariantDraft}
        onGenerateVariantMatrix={vm.generateVariantMatrix}
        onPickVariantImage={vm.handlePickVariantImage}
        onRemoveVariantImage={vm.handleRemoveVariantImage}
        variantImageUploadingKey={vm.variantImageUploadingKey}
        onPickAxisImage={vm.handlePickAxisImage}
        onRemoveAxisImage={vm.handleRemoveAxisImage}
        axisImageUploading={vm.axisImageUploading}
        inactiveVariants={vm.inactiveVariants}
        onReactivateVariant={vm.handleReactivateVariant}
        layoutWide={isWideCatalogLayout}
        imageUrl={vm.draft.imageUrl}
        onPickImage={vm.handlePickImage}
        onRemoveImage={vm.handleRemoveImage}
        imageUploading={vm.imageUploading}
        videoUrl={vm.draft.videoUrl}
        onPickVideo={vm.handlePickVideo}
        onRemoveVideo={vm.handleRemoveVideo}
        videoUploading={vm.videoUploading}
      />

      <BulkCoefficientModal
        visible={vm.bulkPriceVisible}
        bulkCategoryScope={vm.bulkPriceCategoryScope}
        onBulkCategoryScopeChange={vm.setBulkPriceCategoryScope}
        bulkSearch={vm.bulkPriceSearch}
        bulkPercentInput={vm.bulkPricePercentInput}
        bulkApplyTo="prices"
        bulkPreview={vm.bulkPricePreview}
        bulkApplyTotal={vm.bulkPriceApplyTotal}
        bulkScopeCounts={vm.bulkPriceScopeCounts}
        categoryOptions={vm.activeCategoryOptions}
        bulkPreviewLoading={vm.bulkPricePreviewLoading}
        bulkSaving={vm.bulkPriceSaving}
        bulkSaveProgress={vm.bulkPriceSaveProgress}
        onClose={() => vm.setBulkPriceVisible(false)}
        onSearchChange={vm.setBulkPriceSearch}
        onOpenScanner={() => vm.setBulkPriceScannerVisible(true)}
        showBarcodeCameraButton={vm.barcodeAllowCamera}
        onSearchSubmitEditing={vm.barcodeAllowHardware ? vm.handleBulkPriceSearchHardwareSubmit : undefined}
        autoFocusSearchOnWeb={false}
        onPercentChange={vm.setBulkPricePercentInput}
        onPreview={vm.loadBulkPricePreview}
        onConfirm={vm.applyBulkPriceUpdate}
        onClearPreview={() => {
          vm.setBulkPricePreview([]);
          vm.setBulkPriceApplyTotal(0);
        }}
        bulkPriceRounding={vm.bulkPriceRounding}
        onBulkPriceRoundingChange={vm.setBulkPriceRounding}
        idleTitle="Subir o bajar precios"
        idleSubtitle="Varios productos a la vez"
      />

      <CatalogScanners
        barcodeAllowCamera={vm.barcodeAllowCamera}
        searchScannerVisible={vm.searchScannerVisible}
        formScannerVisible={vm.formScannerVisible}
        bulkPriceScannerVisible={vm.bulkPriceScannerVisible}
        onSearchScannerClose={() => vm.setSearchScannerVisible(false)}
        onFormScannerClose={() => vm.setFormScannerVisible(false)}
        onBulkPriceScannerClose={() => vm.setBulkPriceScannerVisible(false)}
        onSearchScan={vm.handleSearchScan}
        onFormScan={vm.handleFormScan}
        onBulkPriceScan={vm.handleBulkPriceScan}
      />

      <CsvImportExportModal {...vm.csvImportModalProps} />

      <CatalogMastersModal
        visible={vm.mastersModalVisible}
        initialSection={vm.mastersInitialSection}
        onClose={vm.closeMastersHub}
      />

      <FilterSheetModal
        visible={filterSheetOpen}
        onClose={() => setFilterSheetOpen(false)}
        onApply={applyFilterDraft}
        onClear={clearFilterDraft}
        appearance="ticket"
        kicker="Catálogo"
        subtitle="Acotá la lista"
        hasActiveDraft={hasActiveDraft}
      >
        {filterDraft ? (
          <CatalogFilterSheetContent
            draft={filterDraft}
            setDraft={(updater) => setFilterDraft((prev) => (prev ? updater(prev) : prev))}
            categoryFilters={vm.categoryFilters}
            getCategoryLabel={vm.getCategoryLabel}
            brandOptions={vm.catalogBrandOptions}
            supplierOptions={vm.catalogSupplierOptions}
            alertsOnPage={vm.kpisAlerts ?? 0}
            incompleteOnPage={vm.kpisIncomplete ?? 0}
            showStockAlertFilter={!vm.hideStockPanels}
          />
        ) : null}
      </FilterSheetModal>
        </>
      }
    />
  );
};
